<template>
  <div>
    <div class="card shadow-sm border-warning">
      <div class="card-header bg-warning text-dark d-flex align-items-center justify-content-between flex-wrap gap-2">
        <span class="fw-semibold">
          <i class="bi bi-robot me-2"></i>Agente de menú semanal
        </span>
        <span class="badge bg-dark">{{ modoLabel }}</span>
      </div>
      <div class="card-body">
        <p class="text-muted small mb-3">
          Las comidas de entre semana salen del plan de rotación.
          Cenas, comidas de finde y cenas de finde salen de las tarjetas compartidas de Trello
          (<strong>Cenas habituales</strong>, <strong>Comidas fin de semana</strong>,
          <strong>Cenas fin de semana</strong>).
          Cada vez que generas, se vuelven a leer esas tarjetas (sin caché).
        </p>

        <div class="mb-4 p-3 border rounded bg-light">
          <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
            <label class="form-label fw-semibold mb-0">
              <i class="bi bi-pin-angle me-1"></i>Reglas fijas (todas las semanas)
            </label>
            <span v-if="guardandoReglas" class="small text-muted">Guardando…</span>
          </div>
          <p class="form-text mt-0 mb-2">
            Se aplican siempre, fuera del cuadro de esta semana. Puedes editarlas
            con el lápiz, sin borrar y volver a crear. Ejemplo:
            <em>El jueves fija como día de Salmorejo para la comida</em>
          </p>
          <div v-if="reglasFijas.length" class="mb-2">
            <div
              v-for="(regla, i) in reglasFijas"
              :key="i"
              class="d-flex align-items-start gap-2 mb-1"
            >
              <template v-if="editandoIdx === i">
                <input
                  :ref="setInputEdicion"
                  class="form-control form-control-sm"
                  v-model="editandoTexto"
                  :disabled="guardandoReglas"
                  @keydown.enter.prevent="guardarEdicionReglaFija"
                  @keydown.escape.prevent="cancelarEdicionReglaFija"
                />
                <button
                  type="button"
                  class="btn btn-sm btn-primary"
                  title="Guardar"
                  :disabled="!editandoTexto.trim() || guardandoReglas"
                  @click="guardarEdicionReglaFija"
                >
                  <i class="bi bi-check-lg"></i>
                </button>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-secondary"
                  title="Cancelar"
                  :disabled="guardandoReglas"
                  @click="cancelarEdicionReglaFija"
                >
                  <i class="bi bi-x-lg"></i>
                </button>
              </template>
              <template v-else>
                <span class="badge bg-warning text-dark text-wrap text-start flex-grow-1">{{ regla }}</span>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-primary"
                  title="Editar"
                  :disabled="guardandoReglas || editandoIdx !== null"
                  @click="empezarEdicionReglaFija(i)"
                >
                  <i class="bi bi-pencil"></i>
                </button>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-danger"
                  title="Quitar"
                  :disabled="guardandoReglas"
                  @click="quitarReglaFija(i)"
                >
                  <i class="bi bi-x"></i>
                </button>
              </template>
            </div>
          </div>
          <p v-else class="small text-muted mb-2">Todavía no hay reglas fijas.</p>
          <div class="d-flex gap-2">
            <input
              class="form-control"
              v-model="nuevaReglaFija"
              placeholder="El jueves fija como día de Salmorejo para la comida"
              @keydown.enter.prevent="anadirReglaFija"
            />
            <button
              type="button"
              class="btn btn-outline-dark"
              :disabled="!nuevaReglaFija.trim() || guardandoReglas"
              @click="anadirReglaFija"
            >
              Añadir
            </button>
          </div>
        </div>

        <form @submit.prevent="generar">
          <div class="row g-3 mb-3">
            <div class="col-md-4">
              <label class="form-label fw-semibold">Desde (inclusive)</label>
              <input type="date" class="form-control" v-model="fechaInicio" required />
            </div>
            <div class="col-md-2">
              <label class="form-label fw-semibold">Días</label>
              <input type="number" class="form-control" v-model.number="dias" min="1" max="14" />
            </div>
          </div>

          <div class="d-flex gap-2 flex-wrap">
            <button type="submit" class="btn btn-warning" :disabled="generando">
              <span v-if="generando" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="bi bi-magic me-1"></i>
              {{ generando ? 'Generando…' : 'Generar menú' }}
            </button>
            <button
              v-if="menu"
              type="button"
              class="btn btn-success"
              :disabled="aceptando || !!regenerandoKey"
              @click="aceptarMenu"
            >
              <span v-if="aceptando" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="bi bi-check2-circle me-1"></i>
              Aceptar menú
            </button>
            <button
              v-if="menu"
              type="button"
              class="btn btn-outline-primary"
              @click="copiarMarkdown"
            >
              <i class="bi bi-clipboard me-1"></i>{{ copiado ? 'Copiado' : 'Copiar menú' }}
            </button>
          </div>
        </form>

        <div v-if="errorAgente" class="alert alert-danger mt-3 mb-0">{{ errorAgente }}</div>
      </div>
    </div>

    <div v-if="menu" class="card shadow-sm mt-3">
      <div class="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
        <span class="fw-semibold">
          <i class="bi bi-calendar-check me-2"></i>Menú propuesto
        </span>
        <small class="text-muted">{{ menu.dias.length }} días · regenera un plato si no te convence</small>
      </div>
      <div class="card-body">
        <div v-if="menu.reglasAplicadas?.length" class="mb-3">
          <div class="small text-uppercase text-muted mb-1">Reglas aplicadas</div>
          <span
            v-for="(regla, i) in menu.reglasAplicadas"
            :key="i"
            class="badge bg-warning text-dark me-1 mb-1"
          >{{ regla }}</span>
        </div>

        <div v-if="menu.reglasFallidas?.length" class="mb-3">
          <div class="small text-uppercase text-danger mb-1">Reglas no aplicadas</div>
          <div
            v-for="(f, i) in menu.reglasFallidas"
            :key="i"
            class="alert alert-danger py-2 px-3 mb-1 small"
          >
            <div class="fw-semibold">{{ f.regla }}</div>
            <div>{{ f.motivo }}</div>
          </div>
        </div>

        <div v-if="menu.avisos?.length" class="alert alert-light border small">
          <div v-for="(aviso, i) in menu.avisos" :key="i">{{ aviso }}</div>
        </div>

        <div v-if="menu.fuentesTrello" class="alert alert-info border small mb-3">
          <div class="fw-semibold mb-1">Leído ahora de Trello</div>
          <div>
            Comidas de finde ({{ (menu.fuentesTrello.comidasFinDeSemana || []).length }}):
            {{ listarPlatos(menu.fuentesTrello.comidasFinDeSemana) }}
          </div>
          <div>
            Cenas de finde ({{ (menu.fuentesTrello.cenasFinDeSemana || []).length }}):
            {{ listarPlatos(menu.fuentesTrello.cenasFinDeSemana) }}
          </div>
          <div>
            Cenas habituales ({{ (menu.fuentesTrello.cenasHabituales || []).length }}):
            {{ listarPlatos(menu.fuentesTrello.cenasHabituales) }}
          </div>
        </div>

        <div class="table-responsive">
          <table class="table align-middle">
            <thead>
              <tr>
                <th>Día</th>
                <th>Plan</th>
                <th>Comida</th>
                <th>Cena</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="dia in menu.dias" :key="dia.fecha">
                <td class="text-nowrap">
                  <div class="fw-semibold text-capitalize">{{ dia.weekday }}</div>
                  <small class="text-muted">{{ formatFecha(dia.fecha) }}</small>
                  <span v-if="dia.esFinDeSemana" class="badge bg-secondary ms-1">Finde</span>
                </td>
                <td class="small">{{ dia.planNombre }}</td>
                <td>
                  <div class="d-flex align-items-center gap-2 flex-nowrap">
                    <span>
                      {{ dia.comida || '—' }}
                      <span v-if="dia.comidaForzada" class="badge bg-warning text-dark ms-1">Regla</span>
                    </span>
                    <button
                      v-if="puedeRegenerar(dia, 'comida')"
                      type="button"
                      class="btn btn-sm btn-outline-secondary"
                      title="Proponer otra comida"
                      :aria-label="`Proponer otra comida para ${dia.weekday}`"
                      :disabled="!!regenerandoKey"
                      @click="regenerar(dia, 'comida')"
                    >
                      <span
                        v-if="regenerandoKey === claveSlot(dia, 'comida')"
                        class="spinner-border spinner-border-sm"
                      ></span>
                      <i v-else class="bi bi-arrow-repeat"></i>
                    </button>
                  </div>
                </td>
                <td>
                  <div class="d-flex align-items-center gap-2 flex-nowrap">
                    <span>
                      {{ dia.cena || '—' }}
                      <span v-if="dia.cenaForzada" class="badge bg-warning text-dark ms-1">Regla</span>
                    </span>
                    <button
                      v-if="puedeRegenerar(dia, 'cena')"
                      type="button"
                      class="btn btn-sm btn-outline-secondary"
                      title="Proponer otra cena"
                      :aria-label="`Proponer otra cena para ${dia.weekday}`"
                      :disabled="!!regenerandoKey"
                      @click="regenerar(dia, 'cena')"
                    >
                      <span
                        v-if="regenerandoKey === claveSlot(dia, 'cena')"
                        class="spinner-border spinner-border-sm"
                      ></span>
                      <i v-else class="bi bi-arrow-repeat"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, nextTick, onMounted, ref } from 'vue';
import axios from '@/config/axios';
import { aplicarEdicionReglaFija, lineasReglas } from '@/utils/nutricionReglas';

function todayIso() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export default {
  name: 'NutricionAgente',
  emits: ['aceptado'],
  setup(_, { emit }) {
    const fechaInicio = ref(todayIso());
    const dias = ref(7);
    const reglasFijas = ref([]);
    const nuevaReglaFija = ref('');
    const editandoIdx = ref(null);
    const editandoTexto = ref('');
    const inputEdicion = ref(null);
    const guardandoReglas = ref(false);
    const generando = ref(false);
    const errorAgente = ref('');
    const menu = ref(null);
    const copiado = ref(false);
    const aceptando = ref(false);
    const regenerandoKey = ref('');

    const modoLabel = computed(() => {
      if (!menu.value) return 'Listo';
      return menu.value.modo === 'openai' ? 'IA (OpenAI)' : 'Reglas + Trello';
    });

    const formatFecha = (fechaStr) => {
      if (!fechaStr) return '';
      return new Date(`${fechaStr}T00:00:00`).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: 'long',
      });
    };

    const listarPlatos = (platos) => {
      const list = (platos || []).map((p) => String(p || '').trim()).filter(Boolean);
      return list.length ? list.join(', ') : 'ninguna';
    };

    const persistirReglasFijas = async (previas) => {
      guardandoReglas.value = true;
      try {
        await axios.put('/api/nutricion/reglas', {
          reglas: reglasFijas.value.join('\n'),
        });
        return true;
      } catch (e) {
        if (Array.isArray(previas)) reglasFijas.value = previas;
        errorAgente.value = e.response?.data?.error || 'Error al guardar las reglas fijas';
        return false;
      } finally {
        guardandoReglas.value = false;
      }
    };

    const cargarReglasFijas = async () => {
      try {
        const { data } = await axios.get('/api/nutricion/reglas');
        reglasFijas.value = lineasReglas(data.reglas);
      } catch {
        reglasFijas.value = [];
      }
    };

    const cancelarEdicionReglaFija = () => {
      editandoIdx.value = null;
      editandoTexto.value = '';
    };

    const anadirReglaFija = async () => {
      const linea = nuevaReglaFija.value.trim();
      if (!linea) return;
      const yaEsta = reglasFijas.value.some((r) => r.toLowerCase() === linea.toLowerCase());
      if (yaEsta) {
        nuevaReglaFija.value = '';
        return;
      }
      const previas = reglasFijas.value;
      reglasFijas.value = [...reglasFijas.value, linea];
      nuevaReglaFija.value = '';
      await persistirReglasFijas(previas);
    };

    const quitarReglaFija = async (idx) => {
      if (editandoIdx.value === idx) {
        cancelarEdicionReglaFija();
      } else if (editandoIdx.value != null && editandoIdx.value > idx) {
        editandoIdx.value = editandoIdx.value - 1;
      }
      const previas = reglasFijas.value;
      reglasFijas.value = reglasFijas.value.filter((_, i) => i !== idx);
      await persistirReglasFijas(previas);
    };

    const setInputEdicion = (el) => {
      inputEdicion.value = el;
    };

    const empezarEdicionReglaFija = async (idx) => {
      errorAgente.value = '';
      editandoIdx.value = idx;
      editandoTexto.value = reglasFijas.value[idx];
      await nextTick();
      inputEdicion.value?.focus?.();
      inputEdicion.value?.select?.();
    };

    const guardarEdicionReglaFija = async () => {
      const idx = editandoIdx.value;
      if (idx == null) return;
      const resultado = aplicarEdicionReglaFija(reglasFijas.value, idx, editandoTexto.value);
      if (resultado.error) {
        errorAgente.value = resultado.error;
        return;
      }
      errorAgente.value = '';
      if (resultado.sinCambios) {
        cancelarEdicionReglaFija();
        return;
      }
      const previas = reglasFijas.value;
      reglasFijas.value = resultado.reglas;
      const ok = await persistirReglasFijas(previas);
      if (ok) cancelarEdicionReglaFija();
    };

    const menuMarkdown = () => {
      if (!menu.value?.dias?.length) return '';
      const lines = ['# Menú semanal', ''];
      for (const dia of menu.value.dias) {
        const marca = dia.esFinDeSemana ? ' (finde)' : '';
        lines.push(`## ${dia.weekday} ${dia.fecha}${marca} — ${dia.planNombre}`);
        lines.push(`- Comida: ${dia.comida || '—'}`);
        lines.push(`- Cena: ${dia.cena || '—'}`);
        lines.push('');
      }
      return lines.join('\n');
    };

    const copiarMarkdown = async () => {
      try {
        await navigator.clipboard.writeText(menuMarkdown());
        copiado.value = true;
        setTimeout(() => { copiado.value = false; }, 2000);
      } catch {
        errorAgente.value = 'No se pudo copiar el menú';
      }
    };

    const aceptarMenu = async () => {
      if (!menu.value?.dias?.length) return;
      aceptando.value = true;
      errorAgente.value = '';
      try {
        await axios.put('/api/nutricion/menu-activo', {
          modo: 'aceptar',
          dias: menu.value.dias,
        });
        emit('aceptado');
      } catch (e) {
        errorAgente.value = e.response?.data?.error || 'Error al aceptar el menú';
      } finally {
        aceptando.value = false;
      }
    };

    const claveSlot = (dia, slot) => `${dia.fecha}-${slot}`;

    const puedeRegenerar = (dia, slot) => {
      if (!dia?.[slot] || dia[`${slot}Forzada`]) return false;
      return true;
    };

    const regenerar = async (dia, slot) => {
      if (!menu.value?.dias?.length || !puedeRegenerar(dia, slot)) return;
      regenerandoKey.value = claveSlot(dia, slot);
      errorAgente.value = '';
      try {
        const { data } = await axios.post('/api/nutricion/agente/regenerar', {
          fechaInicio: fechaInicio.value,
          dias: dias.value,
          fecha: dia.fecha,
          slot,
          menu: menu.value.dias,
        });
        menu.value = data;
      } catch (e) {
        errorAgente.value = e.response?.data?.error || 'Error al regenerar la comida';
      } finally {
        regenerandoKey.value = '';
      }
    };

    const generar = async () => {
      generando.value = true;
      errorAgente.value = '';
      copiado.value = false;
      try {
        const { data } = await axios.post('/api/nutricion/agente', {
          fechaInicio: fechaInicio.value,
          dias: dias.value,
        });
        menu.value = data;
      } catch (e) {
        menu.value = null;
        errorAgente.value = e.response?.data?.error || 'Error al generar el menú';
      } finally {
        generando.value = false;
      }
    };

    onMounted(cargarReglasFijas);

    return {
      fechaInicio,
      dias,
      reglasFijas,
      nuevaReglaFija,
      editandoIdx,
      editandoTexto,
      setInputEdicion,
      guardandoReglas,
      generando,
      errorAgente,
      menu,
      copiado,
      aceptando,
      regenerandoKey,
      modoLabel,
      formatFecha,
      listarPlatos,
      anadirReglaFija,
      quitarReglaFija,
      empezarEdicionReglaFija,
      guardarEdicionReglaFija,
      cancelarEdicionReglaFija,
      copiarMarkdown,
      aceptarMenu,
      claveSlot,
      puedeRegenerar,
      regenerar,
      generar,
    };
  },
};
</script>
